import { watch, reactive, ref, computed } from '@vue/composition-api';
import { prepareSchema } from '@/composables/services/prepareSchema';

const campaignSort = (a, b) => (a.label > b.label ? 1 : -1);

export const useFormValues = ({ store, template }) => {
  const companyCampaigns = computed(() => store.state.company.campaign.list);
  const segmentCampaigns = computed(
    () => store.state.company.segment.campaign.list
  );
  const domainUrls = computed(() => store.state.company.domain.list);
  const company = computed(() => store.getters['company/currentCompany']);
  const segment = computed(
    () => store.getters['company/segment/currentSegment']
  );
  const formValues = reactive({
    name: '',
    campaign: '',
    target_url: '',
    tracking_param: '',
    client_data: [{}],
  });
  const templateChanged = ref(false);

  watch(template, (templateValue) => {
    if (!templateValue) {
      return;
    }
    formValues.name = templateValue.name || '';
    formValues.campaign = templateValue.campaign || '';
    formValues.target_url = templateValue.target_url || '';
    formValues.tracking_param = templateValue.tracking_param || '';
    formValues.client_data = [templateValue.client_data];
    templateChanged.value = true;
  });

  const targetUrlDomain = computed(() => {
    const value = formValues.target_url;
    if (!value) {
      return '';
    }
    return domainUrls.value.find((d) => value.includes(d.name));
  });
  const targetUrlError = computed(() =>
    targetUrlDomain.value || targetUrlDomain.value === ''
      ? null
      : `Given URL doesn't match the domain whitelist.`
  );
  const domainParameterOptions = computed(() => {
    if (!targetUrlDomain.value) {
      return [];
    }
    if (targetUrlDomain.value.parameter_list.length === 1) {
      formValues.tracking_param = targetUrlDomain.value.parameter_list[0].value;
    }
    return targetUrlDomain.value.parameter_list.map(({ value }) => ({
      label: value,
      value,
    }));
  });
  const domainParameterPlaceholder = computed(() => {
    if (!formValues.target_url || targetUrlError.value) {
      return 'Set Target URL first';
    }
    if (domainParameterOptions.length === 0) {
      return 'This Target URL has no Tracking Parameters';
    }
    return 'Select Tracking Parameter';
  });
  const campaignOptions = computed(() => {
    const options = {};
    if (segmentCampaigns.value.length) {
      const campaignOptions = segmentCampaigns.value.map((campaign) => ({
        value: campaign.id,
        label: campaign.name,
      }));
      campaignOptions.sort(campaignSort);
      options[`${segment.value.name} Campaigns`] = campaignOptions;
    }
    if (companyCampaigns.value.length) {
      const campaignOptions = companyCampaigns.value.map((campaign) => ({
        value: campaign.id,
        label: campaign.name,
      }));
      campaignOptions.sort(campaignSort);
      options[`${company.value.name} Campaigns`] = campaignOptions;
    }
    // console.log(options);
    return options;
  });
  const selectedCampaignName = computed(() => {
    if (!formValues.campaign) {
      return '';
    }
    const campaignValue = Number(formValues.campaign);
    const possibleCampaign = segmentCampaigns.value.find(
      (campaign) => campaign.id === campaignValue
    );
    if (possibleCampaign) {
      return possibleCampaign.name;
    }
    const possibleCompanyCampaign = companyCampaigns.value.find(
      (campaign) => campaign.id === campaignValue
    );
    if (possibleCompanyCampaign) {
      return possibleCompanyCampaign.name;
    }
    return '';
  });
  const schema = computed(() => {
    const segmentSchema = store.state.company.segment.schema.list;
    return segmentSchema.length > 0
      ? prepareSchema(segmentSchema)
      : prepareSchema(store.state.company.schema.list);
  });

  watch(targetUrlDomain, (val, prevVal) => {
    // block tracking param reset on template changes
    // (including initial)
    if (templateChanged.value) {
      templateChanged.value = false;
      return;
    }
    if (val !== prevVal) {
      formValues.tracking_param = '';
    }
  });

  return {
    formValues,
    targetUrlError,
    domainParameterOptions,
    domainParameterPlaceholder,
    campaignOptions,
    selectedCampaignName,
    schema,
  };
};
